import { Button, Flex, FormControl, FormLabel, Heading, Select } from '@chakra-ui/react'
import { getRevenueRecordings } from 'api'
import { UiCard, UiCardHeader, UiCardBody, UiDataTable } from 'components/common'
import { useAppContext } from 'hooks/useAppContext'
import { useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'
import { PREVIOUS_REVENUE_COLUMNS } from './data'
import { getMonth, getYear } from 'date-fns'
import { ROLES } from 'lib'

const MONTH_LABELS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
export const PreviousRevenueTable = (props) => {
  const { currentUser, createToast } = useAppContext()
  const [isLoading, setIsLoading] = useState(false)
  const [year, setYear] = useState(getYear(new Date()))
  const [month, setMonth] = useState(getMonth(new Date()) + 1)
  const [revenues, setRevenues] = useState([])
  const [tableConfig, setTableConfig] = useImmer({
    curPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 10,
    search: '',
    orderBy: {
      key: 'name',
      direction: 'asc'
    }
  })

  const loadRevenueRecordings = async () => {
    setIsLoading(true)
    try {
      const response = await getRevenueRecordings(year, month)
      if (currentUser?.role === ROLES.Admin) {
        setRevenues(response)
      } else if (currentUser?.role === ROLES.SalesUser) {
        setRevenues(response.filter(v => v.name === currentUser?.name))
      } else {
        setRevenues([])
      }
      setIsLoading(false)
    } catch (err) {
      createToast('error', 'Error', 'Failed to load previous revenue recordings.')
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadRevenueRecordings()
  }, [])

  useEffect(() => {
    setTableConfig(draft => {
      draft.totalRecords = revenues.length || 0
      draft.totalPages = Math.ceil(draft.totalRecords / draft.itemsPerPage)
    })
  }, [revenues])

  const customizedRows = useMemo(() => {
    const searchedData = revenues.filter(v => v.name.includes(tableConfig.search))
    const sortedData = searchedData.sort((a, b) => {
      const direction = tableConfig.orderBy.direction === 'asc' ? 1 : -1
      return a[tableConfig.orderBy.key] < b[tableConfig.orderBy.key]
        ? 1 * direction
        : (a[tableConfig.orderBy.key] === b[tableConfig.orderBy.key] ? 0 : -1 * direction)
    })
    const dataForPage = sortedData.slice((tableConfig.curPage - 1) * tableConfig.itemsPerPage, tableConfig.curPage * tableConfig.itemsPerPage)
    return dataForPage
  }, [revenues, tableConfig.curPage, tableConfig.itemsPerPage, tableConfig.search, tableConfig.orderBy.key, tableConfig.orderBy.direction])

  const YEARS = useMemo(() => {
    const currentYear = getYear(new Date())
    const arr = []
    // eslint-disable-next-line space-unary-ops
    for (let i = 2020; i <= currentYear; i ++) {
      arr.push(i)
    }
    return arr
  }, [])

  const MONTHS = useMemo(() => {
    const currentYear = getYear(new Date())
    const arr = []
    // eslint-disable-next-line space-unary-ops
    for (let i = 1; i <= (year === currentYear ? (getMonth(new Date()) + 1) : 12); i ++) {
      arr.push(i)
    }
    return arr
  }, [year])

  return (
    <UiCard borderRadius={0} {...props}>
      <UiCardHeader bg="gray.500" p={4}>
        <Heading color="white" size="sm">Previous Revenue Records</Heading>
      </UiCardHeader>
      <UiCardBody p={8}>
        <Flex gap={4} alignItems="flex-end" mb={8}>
          <FormControl flex={1}>
            <FormLabel>Year</FormLabel>
            <Select name="year" value={year} onChange={e => setYear(e.target.value)}>
              {YEARS.map(v => <option key={v} value={v}>{v}</option>)}
            </Select>
          </FormControl>
          <FormControl flex={1}>
            <FormLabel>Month</FormLabel>
            <Select name="salespersonId" value={month} onChange={e => setMonth(e.target.value)}>
              {MONTHS.map(v => <option key={v} value={v}>{MONTH_LABELS[v - 1]}</option>)}
            </Select>
          </FormControl>
          <Button size="sm" colorScheme="blue" flex={1} onClick={loadRevenueRecordings}>View</Button>
        </Flex>
        <UiDataTable
          tableConfig={tableConfig}
          updateTableConfig={setTableConfig}
          rows={customizedRows}
          rowKey="name"
          columns={PREVIOUS_REVENUE_COLUMNS}
          isLoading={isLoading}
        />
      </UiCardBody>
    </UiCard>
  )
}
